import { Component, Inject, OnInit } from '@angular/core';
import { Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ModalService } from '@assetaccountant/core/services/modal.service';
import { HasTitle } from '@assetaccountant/core/services/title.service';
import { TypedActivatedRoute } from '@assetaccountant/core/utils/route-utils';
import { emptyValue, TypedFormBuilder } from '@assetaccountant/core/utils/typed-forms';
import { DynamicsCompany } from '@assetaccountant/integrations/models/dynamics/dynamics-company';
import { DynamicsEnvironment } from '@assetaccountant/integrations/models/dynamics/dynamics-environment';
import { OidcStateParams } from '@assetaccountant/integrations/models/oidc-state-params';
import { MicrosoftDynamicsService } from '@assetaccountant/integrations/services/dynamics.service';

@Component({
  selector: 'app-dynamics-select-environment',
  templateUrl: './dynamics-select-environment.component.html',
  styleUrls: ['./dynamics-select-environment.component.scss'],
  standalone: false,
})
export class DynamicsSelectEnvironmentComponent implements OnInit, HasTitle {
  environments: DynamicsEnvironment[];
  isLoaded = false;
  isLoadingCompanies = false;
  isConnecting = false;
  isNewConnection = false;
  isReconnect = false;
  returnRoute: URL;
  registerId: number;
  tenantId: string;
  companies: DynamicsCompany[];

  form = this.fb.group({
    environmentName: [{ value: '', disabled: !this.isLoaded }, Validators.required],
    organisationId: [emptyValue<number>()],
    registerId: [emptyValue<number>()],
    companyId: [{ value: '', disabled: !this.isLoadingCompanies }, Validators.required],
  });

  get currentYear() {
    return new Date().getFullYear();
  }

  companyLabel(company: DynamicsCompany): string {
    const name = company.name?.trim();
    const displayName = company.displayName?.trim();
    if (name && displayName && name !== displayName) {
      return `${name} (${displayName})`;
    }
    return name || displayName || 'Unknown';
  }

  get title() {
    return { pageTitle: 'Select Dynamics Environment' };
  }

  constructor(
    private fb: TypedFormBuilder,
    private router: Router,
    @Inject(ActivatedRoute)
    private route: TypedActivatedRoute<{}, {}, { code: string; state: string; error: string }>,
    private modalService: ModalService,
    private dynamicsService: MicrosoftDynamicsService,
  ) {}

  ngOnInit() {
    this.route.queryParams.subscribe(params => {
      const localState = localStorage.getItem('aa-oidc-state');

      if (params && localState === params.state) {
        const stateParams: OidcStateParams = JSON.parse(atob(params.state));
        if (params.error) {
          location.href = new URL(stateParams.returnRoute, location.origin).toString();
          return;
        }

        const environmentMatch = /\environmentName=([^#]*)/.exec(stateParams.returnRoute);
        const companyMatch = /\companyId=([^#]*)/.exec(stateParams.returnRoute);
        this.returnRoute = new URL(stateParams.returnRoute, location.origin);
        if (this.returnRoute.searchParams.has('environmentName')) {
          this.returnRoute.searchParams.delete('environmentName');
          this.isReconnect = true;
        }
        if (this.returnRoute.searchParams.has('companyId')) {
          this.returnRoute.searchParams.delete('companyId');
          this.isReconnect = true;
        }

        const redirectMatch = /\bregisters\/(\d+)/.exec(stateParams.returnRoute);
        const registerId = redirectMatch && redirectMatch[1];

        if (registerId == null) {
          this.isNewConnection = true;
        } else {
          this.registerId = +registerId;
        }

        this.dynamicsService.listEnvironments(params.code).subscribe({
          next: listEnvironmentsResponse => {
            this.tenantId = listEnvironmentsResponse.tenantId;
            this.form.controls.environmentName.enable();
            this.environments = listEnvironmentsResponse.environments;

            if (this.environments.length === 0) {
              this.modalService
                .showErrorModal(
                  'No available environments found, please contact support.',
                  'No Environments Found',
                )
                .subscribe(() => this.onCancelClick());
            } else if (this.isReconnect && environmentMatch && environmentMatch[1]) {
              const previouslyConnectedEnvironment = this.environments.find(
                t => t.name === environmentMatch[1],
              );

              if (previouslyConnectedEnvironment) {
                this.form.controls.environmentName.setValue(environmentMatch[1].toString());
                this.isLoaded = true;
                this.onEnvironmentSelected(companyMatch);
                return;
              }

              this.modalService
                .showQuestionModal(
                  "<p>We've noticed that you are refreshing a previously established connection but we couldn't find your previously connected environment from Business Central.</p> <p>Please ensure you are using the correct Microsoft account to reconnect.</p>",
                  'Previous Environment Not Found',
                  'Cancel Reconnection',
                  'Continue Reconnecting',
                )
                .subscribe(() => this.onCancelClick());
            } else if (this.environments.length === 1) {
              this.form.controls.environmentName.setValue(this.environments[0].name.toString());
              this.onEnvironmentSelected(companyMatch);
            }
            this.isLoaded = true;
          },
          error: error => {
            location.href = stateParams.returnRoute;
            throw error;
          },
        });
      } else {
        this.router.navigate(['/organisations/-']);
      }
    });
  }

  onEnvironmentSelected(companyMatch?) {
    this.isLoadingCompanies = true;
    this.dynamicsService.listCompanies(this.tenantId, this.form.value.environmentName).subscribe({
      next: listCompaniesResponse => {
        this.form.controls.companyId.enable();
        this.companies = listCompaniesResponse.companies;

        if (this.companies.length === 0) {
          this.modalService
            .showErrorModal(
              'No available companies found, please contact support.',
              'No Companies Found',
            )
            .subscribe(() => this.onCancelClick());
        } else if (this.isReconnect && companyMatch && companyMatch[1]) {
          const previouslyConnectedCompany = this.companies.find(t => t.name === companyMatch[1]);

          if (previouslyConnectedCompany) {
            this.form.controls.companyId.setValue(companyMatch[1].toString());
            this.isLoadingCompanies = false;
            return;
          }

          this.modalService
            .showQuestionModal(
              "<p>We've noticed that you are refreshing a previously established connection but we couldn't find your previously connected company from Business Central.</p> <p>Please ensure you are using the correct Microsoft account to reconnect.</p>",
              'Previous Company Not Found',
              'Cancel Reconnection',
              'Continue Reconnecting',
            )
            .subscribe(() => this.onCancelClick());
        } else if (this.companies.length === 1) {
          this.form.controls.companyId.setValue(this.companies[0].id.toString());
        }

        this.isLoadingCompanies = false;
      },
      error: error => {
        location.href = this.returnRoute.toString();
        throw error;
      },
    });
  }

  onConnectClick() {
    this.isConnecting = true;
    this.form.disable();

    const command = {
      tenantId: this.tenantId,
      registerId: this.registerId || +this.form.value.registerId || undefined,
      organisationId: +this.form.value.organisationId || undefined,
      environment: this.form.value.environmentName,
      companyId: this.form.value.companyId,
      companyNameShort: this.companies?.find(c => c.id === this.form.value.companyId)?.name ?? '',
    };

    this.dynamicsService.connect(command).subscribe({
      next: response => {
        this.isConnecting = false;
        if (this.isNewConnection) {
          this.router.navigateByUrl(
            `/organisations/${response.integration.organisationId}/registers/${response.integration.registerId}/settings#integrations`,
          );
        } else {
          location.href = this.returnRoute.toString();
        }
        return;
      },
      error: error => {
        this.isConnecting = false;
        this.form.enable();
        this.dynamicsService.handleError(command.registerId, error, this.router.url);
      },
    });
  }

  onCancelClick() {
    if (this.isNewConnection) {
      location.href = this.returnRoute.toString();
      return;
    }

    this.router.navigateByUrl(
      `/organisations/-/registers/${this.registerId}/settings#integrations`,
    );
    return;
  }
}
